/**
 * Dashboard-route reconciler.
 *
 * Authoritative reconciliation: the backend is the source of truth.
 *   - For every IDB project that has been synced before (`syncedToBackend=true`
 *     AND `projectId` set) AND is no longer in the backend's listing → DELETE
 *     from IDB and localStorage list.
 *   - Untouched: IDB projects without `syncedToBackend` — these are
 *     just-created drafts the user might still be editing. Deleting them
 *     would lose unsaved work.
 *
 * Called on:
 *   1. App boot, after auth resolves (via syncLocalProjectsToBackend, which
 *      does the same reconciliation inline before its sync loop).
 *   2. Every time the user navigates back to the dashboard route.
 *   3. Window focus (catches: user deletes a project in another tab, then
 *      switches back to Studio Pro).
 *
 * Cheap: one GET /api/video-projects?project_type=studio_pro returns only
 * metadata (no project_data payload), so this is safe to run frequently.
 */

import { getProjectsFromBackend } from './backendService';
import { getAllLocalProjects, deleteProjectFromIndexedDB } from './dbService';

export async function reconcileLocalProjectsWithBackend() {
    const remoteRes = await getProjectsFromBackend();
    if (!remoteRes.success) {
        // Network / 401 — leave IDB alone, try again later.
        console.warn('Dashboard reconciliation skipped:', remoteRes.error);
        return { kept: 0, dropped: 0, droppedIds: [], skipped: true };
    }
    const remoteIds = new Set(remoteRes.projects.map(p => p.id));

    const local = await getAllLocalProjects();
    let dropped = 0;
    let kept = 0;
    const droppedIds = [];
    for (const lp of local) {
        if (!lp?.id) continue;
        // Only prune rows that have been observed on the backend at least
        // once. Brand-new local drafts without syncedToBackend might be
        // work-in-progress the user hasn't yet pushed — never delete those.
        if (!lp.syncedToBackend || !lp.projectId) { kept += 1; continue; }
        if (remoteIds.has(lp.projectId)) { kept += 1; continue; }

        try {
            await deleteProjectFromIndexedDB(lp.id);
            droppedIds.push(lp.id);
            dropped += 1;
            console.log(`🪦 Reconciler: pruned tombstoned project ${lp.projectId} (localId=${lp.id})`);
        } catch (e) {
            console.warn('Reconcile delete failed for', lp.id, e?.message);
        }
    }
    return { kept, dropped, droppedIds, skipped: false };
}
